import { useCallback, useMemo, useState } from 'react';
import { Box, Button, Collapse, ListItem, Theme, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChildrenProps, IMenuItem, IMenuLeaf } from '@dfl/mui-react-common';
import { Link } from 'react-router-dom';
import { useMenuContext } from 'settings/main-menu/context/useMenuContext';

// @ts-ignore
const backgroundItem = (theme: Theme) => theme.palette.sidebar.active || theme.palette.primary.light;
// @ts-ignore
const activeColor = (theme: Theme) => theme.palette.sidebar.activeColor || 'secondary.main';
// @ts-ignore
const color = (theme: Theme) => theme.palette.sidebar.color || 'secondary.main';

type SidebarItemProps = Omit<IMenuItem, 'children'> &
IMenuLeaf &
ChildrenProps & { depth: number; open?: boolean; active: boolean };

export const SidebarItem = ({
  active = false,
  children,
  chip,
  depth,
  icon,
  info,
  open = false,
  disabled,
  path,
  title,
  ...other
}: SidebarItemProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const theme = useTheme();
  const isResp = useMediaQuery(theme.breakpoints.down('sm'));
  const { onClose } = useMenuContext();

  const handleToggle = () => {
    setIsOpen((prevOpen) => !prevOpen);
  };

  const handleMobileClose = useCallback(() => {
    if (isResp) {
      onClose?.();
    }
  }, [isResp, onClose]);

  const paddingLeft = useMemo(() => (depth > 0 ? 32 * depth : 24), [depth]);

  // Branch
  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{
          display: 'block',
          mb: 0.5,
          py: 0,
          px: 2,
        }}
        {...other}
      >
        <Button
          endIcon={!isOpen ? <ChevronRightIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
          disabled={disabled}
          disableRipple
          onClick={handleToggle}
          startIcon={icon}
          sx={{
            color,
            justifyContent: 'flex-start',
            pl: `${paddingLeft}px`,
            pr: 3,
            textAlign: 'left',
            textTransform: 'none',
            width: '100%',
            ...(active && {
              backgroundColor: backgroundItem,
              color: activeColor,
              fontWeight: 'fontWeightBold',
            }),
            '&:hover': {
              backgroundColor: backgroundItem,
              color: activeColor,
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>{title}</Box>
          {info}
        </Button>
        <Collapse in={isOpen} sx={{ mt: 0.5 }}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  // Leaf
  return (
    <ListItem
      data-tour={`step-menu-${path}`}
      disableGutters
      sx={{
        display: 'flex',
        mb: 0.5,
        py: 0,
        px: 2,
      }}
    >
      <Button
        component={Link}
        to={path}
        startIcon={icon}
        endIcon={chip}
        disabled={disabled}
        disableRipple
        onClick={handleMobileClose}
        sx={{
          borderRadius: 1,
          // color: "neutral.300",
          justifyContent: 'flex-start',
          pl: `${paddingLeft}px`,
          pr: 3,
          textAlign: 'left',
          textTransform: 'none',
          color,
          width: '100%',
          ...(active && {
            backgroundColor: backgroundItem,
            color: activeColor,
            fontWeight: 'fontWeightBold',
          }),
          // '& .MuiButton-startIcon': {
          //
          // },
          '&:hover': {
            backgroundColor: backgroundItem,
            color: activeColor,
          },
        }}
      >
        <Box sx={{ flexGrow: 1 }}>{title}</Box>
        {info}
      </Button>
    </ListItem>
  );
};
